import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, Button } from 'react-bootstrap';
import { useAuth } from '@/contexts/AuthContext';
import { ROLES, ROLE_LABELS } from '@/auth/roles';
import type { AppRole } from '@/types';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { BrandMark } from '@/components/shared/BrandMark';

/** Requires an authenticated user; redirects to Auth0 login otherwise. */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, login, role, logout } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingSpinner fullPage label="Checking your session..." />;

  if (!isAuthenticated) {
    sessionStorage.setItem('postLoginRedirect', location.pathname + location.search);
    return (
      <div className="d-flex flex-column align-items-center justify-content-center vh-100 gap-4 px-4">
        <BrandMark iconHeight={48} fontSizeClass="fs-1" />
        <p className="text-muted text-center mb-0" style={{ maxWidth: 360 }}>
          Sign in to access your competitive messaging intelligence dashboard.
        </p>
        <Button onClick={login}>Sign in</Button>
      </div>
    );
  }

  // Authenticated but no recognized crm:* role on the token: the backend will
  // reject every API call, so show a friendly explanation instead of a wall of
  // failed requests.
  if (role === null) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center vh-100 gap-3 px-4">
        <Alert variant="warning" className="text-center" style={{ maxWidth: 480 }}>
          <Alert.Heading>No role assigned</Alert.Heading>
          <p className="mb-0">
            Your account has no role assigned in CRM Intelligence. Please contact an administrator
            to request access.
          </p>
        </Alert>
        <Button variant="outline-secondary" onClick={logout}>
          Sign out
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Requires the user's role to rank at or above `minimum`.
 * Assumes it is rendered inside a ProtectedRoute (authentication + no-role
 * handling already done), but still guards against unauthenticated access.
 */
export function RequireRole({ minimum, children }: { minimum: AppRole; children: ReactNode }) {
  const { isLoading, isAuthenticated, role, isAdmin, isEditorOrAbove, isAnalystOrAbove } =
    useAuth();

  if (isLoading) return <LoadingSpinner fullPage label="Checking permissions..." />;
  if (!isAuthenticated) return <Navigate to="/" replace />;

  const allowed =
    minimum === ROLES.Admin
      ? isAdmin
      : minimum === ROLES.Editor
        ? isEditorOrAbove
        : minimum === ROLES.Analyst
          ? isAnalystOrAbove
          : role !== null;

  if (!allowed) {
    return (
      <Alert variant="danger" className="m-4">
        <Alert.Heading>Access denied</Alert.Heading>
        <p className="mb-0">
          This area requires the {ROLE_LABELS[minimum]} role (or higher). If you believe you should
          have access, contact your administrator.
        </p>
      </Alert>
    );
  }

  return <>{children}</>;
}

/** Requires the crm:admin role in addition to authentication. */
export function AdminRoute({ children }: { children: ReactNode }) {
  return <RequireRole minimum={ROLES.Admin}>{children}</RequireRole>;
}

/** Requires crm:editor or higher — used for Teams and User Management. */
export function EditorRoute({ children }: { children: ReactNode }) {
  return <RequireRole minimum={ROLES.Editor}>{children}</RequireRole>;
}

/** Requires crm:analyst or higher — used for the AI tools. */
export function AnalystRoute({ children }: { children: ReactNode }) {
  return <RequireRole minimum={ROLES.Analyst}>{children}</RequireRole>;
}
